import * as React from 'react';
import './App.css';
import {createBrowserRouter, Navigate, RouterProvider, useLoaderData} from 'react-router-dom';
import i18n from './i18n/i18n';
import {I18nextProvider} from 'react-i18next';
import Root from './routes/Root';
import PcBridgePage from './routes/pc/PcBridgePage';
import TimerPage from './routes/pc/TimerPage';
import DonePage from './routes/pc/DonePage';
import CancelPage from './routes/pc/CancelPage';
import ErrorPage from './routes/pc/ErrorPage';
import TimeoutPage from './routes/pc/TimeoutPage';
import Transactions from './routes/Transactions';
import LanguagePage from './routes/pc/LanguagePage';
import MobileBridgePage from './routes/mobile/MobileBridgePage';
import MobileErrorPage from './routes/mobile/MobileErrorPage';
import MobileInstallPage from './routes/mobile/MobileInstallPage';
import MobileLanguagePage from './routes/mobile/MobileLanguagePage';
import {useEffect} from 'react';
import {string} from 'prop-types';
import WebViewBridgePage from './routes/webview/WebViewBridgePage';
import WebViewTransactions from './routes/webview/WebViewTransactions';
import WebViewErrorPage from './routes/webview/WebViewErrorPage';
import UpdateAppPage from './routes/pc/UpdateAppPage';
import WebViewUpdateAppPage from "./routes/webview/WebViewUpdateAppPage";
import PcBridgeForStageWithoutApkPage from "./routes/pc/PcBridgeForStageWithoutApkPage";
import MobileBridgeForStageWithoutApkPage from "./routes/mobile/MobileBridgeForStageWithoutApkPage";
import MobileIframeTransactions from './routes/mobile/MobileIframeTransactions';

function NavigateToWithLanguageAndParameters({pathBeforeLanguage, pathAfterLanguage}) {
  NavigateToWithLanguageAndParameters.propTypes = {
    pathBeforeLanguage: string,
    pathAfterLanguage: string
  };

  const {searchParams} = useLoaderData();
  const urlSearchParams = new URLSearchParams(searchParams);
  
  if (urlSearchParams.has("isFromQr")) {
    return (
      <Navigate to={`${pathBeforeLanguage}/${i18n.language}${pathAfterLanguage}${searchParams}`} replace />
    );
  } 
  
  return (
    <Navigate to={`${pathBeforeLanguage}/${i18n.language}${pathAfterLanguage}${searchParams}`} />
  );
}

async function queryParameterLoader({request}) {
  const url = new URL(request.url);
  const searchParams = url.search;

  return {searchParams};
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <Root />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: '/gsmpi-web/transactions',
        element: <Transactions />
      },
      {
        path: '/gsmpi-web/binding',
        element: <PcBridgePage />
      },
      {
        path: '/gsmpi-web/language',
        element: <LanguagePage />
      },
      {
        path: '/gsmpi-web/time-remaining',
        element: <TimerPage />
      },
      {
        path: '/gsmpi-web/done',
        element: <DonePage />
      },
      {
        path: '/gsmpi-web/cancel',
        element: <CancelPage />
      },
      {
        path: '/gsmpi-web/error',
        element: <ErrorPage />
      },
      {
        path: '/gsmpi-web/timeout',
        element: <TimeoutPage />
      },
      {
        path: '/gsmpi-web/update',
        element: <UpdateAppPage />
      },
      {
        path: '/gsmpi-web/mobile/transactions',
        element: <MobileIframeTransactions />
      },
      {
        path: '/gsmpi-web/mobile/bridge',
        loader: queryParameterLoader,
        element: <NavigateToWithLanguageAndParameters pathBeforeLanguage="/gsmpi-web/mobile" pathAfterLanguage="/bridge" />
      },
      {
        path: '/gsmpi-web/mobile/:lang/bridge',
        element: <MobileBridgePage />
      },
      {
        path: '/gsmpi-web/mobile/language',
        loader: queryParameterLoader,
        element: <NavigateToWithLanguageAndParameters pathBeforeLanguage="/gsmpi-web/mobile" pathAfterLanguage="/language" />
      },
      {
        path: '/gsmpi-web/mobile/:lang/language',
        element: <MobileLanguagePage/>
      },
      {
        path: '/gsmpi-web/mobile/install',
        element: <Navigate to={`/gsmpi-web/mobile/${i18n.language}/install`}/>
      },
      {
        path: '/gsmpi-web/mobile/:lang/install',
        element: <MobileInstallPage />
      },
      {
        path: '/gsmpi-web/mobile/error',
        loader: queryParameterLoader,
        element: <NavigateToWithLanguageAndParameters pathBeforeLanguage="/gsmpi-web/mobile" pathAfterLanguage="/error" />,
      },
      {
        path: '/gsmpi-web/mobile/:lang/error',
        element: <MobileErrorPage/>
      },
      {
        path: '/gsmpi-web/webview/transactions',
        element: <WebViewTransactions/>
      },
      {
        path: '/gsmpi-web/webview/bridge',
        loader: queryParameterLoader,
        element: <NavigateToWithLanguageAndParameters pathBeforeLanguage="/gsmpi-web/webview" pathAfterLanguage="/bridge" />
      },
      {
        path: '/gsmpi-web/webview/:lang/bridge',
        element: <WebViewBridgePage />
      },
      {
        path: '/gsmpi-web/webview/error',
        element: <Navigate to={`/gsmpi-web/webview/${i18n.language}/error`}/>
      },
      {
        path: '/gsmpi-web/webview/:lang/error',
        element: <WebViewErrorPage />
      },
      {
        path: '/gsmpi-web/webview/update',
        element: <Navigate to={`/gsmpi-web/webview/${i18n.language}/update`}/>
      },
      {
        path: '/gsmpi-web/webview/:lang/update',
        element: <WebViewUpdateAppPage />
      },
      {
        path: '/gsmpi-web/test-transactions/bridge',
        element: <PcBridgeForStageWithoutApkPage/>
      },
      {
        path: '/gsmpi-web/mobile/test-transactions/bridge',
        loader: queryParameterLoader,
        element: <NavigateToWithLanguageAndParameters pathBeforeLanguage="/gsmpi-web/mobile" pathAfterLanguage="/test-transactions/bridge" />
      },
      {
        path: '/gsmpi-web/mobile/:lang/test-transactions/bridge',
        element: <MobileBridgeForStageWithoutApkPage/>
      }
    ]
  },
]);

function App() {
  useEffect(() => {
    const updateDirAttribute = () => {
      const direction = i18n.dir(i18n.language);
      document.documentElement.setAttribute('dir', direction);
    };
    updateDirAttribute();
    i18n.on('languageChanged', updateDirAttribute);

    return () => {
      i18n.off('languageChanged', updateDirAttribute);
    };
  }, [i18n]);

  return (
    <I18nextProvider i18n={i18n}>
      <RouterProvider router={router} />
    </I18nextProvider>
  );
}

export default App;
